import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import ProfileForm from "@/components/ProfileForm";
import CollegeSelect from "@/components/CollegeSelect";
import ResidencyToggle from "@/components/ResidencyToggle";
import Spinner from "@/components/Spinner";
import { MissionEyebrow, StickerVsNet } from "@/components/Mission";
import { getProfile, profileForApi } from "@/lib/profileStore";

const VERDICT = {
  very_realistic: { label: "Very realistic", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  realistic: { label: "Realistic", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  stretch: { label: "Stretch", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  long_shot: { label: "Long shot — but possible", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};
const APPEAL = {
  high: "High — expect movement",
  medium: "Medium — worth the ask",
  low: "Low — but free to try",
  none: "Likely none — focus on outside scholarships",
};

export default function DreamSchoolPage() {
  const [params] = useSearchParams();
  const [profile, setProfileLocal] = useState(getProfile());
  const [school, setSchool] = useState(params.get("school") || "");
  const [residency, setResidency] = useState("");
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [plan, setPlan] = useState(null);

  const run = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!school || school.trim().length < 2) return setError("Type the school name.");
    if (!residency) return setError("Pick In-State or Out-of-State for this school.");
    setBusy(true); setPlan(null);
    try {
      const r = await api.post("/aw/saver/dream", {
        profile: profileForApi(profile),
        school: school.trim(),
        residency,
        why_dream: why.trim() || null,
      });
      setPlan(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't run the rescue plan. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <AppShell>
      <div className="py-10" data-testid="dream-root">
        <MissionEyebrow testId="dream-mission" />
        <h1 className="font-heading italic text-4xl sm:text-5xl text-[#0f172a] mb-3">Dream School Rescue</h1>
        <p className="text-slate-600 mb-8 max-w-2xl">
          Type the school you think you can't afford. We'll return your real path —
          net price, scholarships, appeal angle, transfer route — quantified in dollars.
        </p>

        <ProfileForm profile={profile} onChange={setProfileLocal} compact />

        <form onSubmit={run} className="aw-card bg-white border border-[#bfdbfe] rounded-2xl p-6 sm:p-8 mb-8" data-testid="dream-form">
          <label className="block text-xs font-medium text-slate-500 mb-1.5">Dream school</label>
          <div className="mb-4">
            <CollegeSelect
              value={school}
              onChange={setSchool}
              placeholder="e.g. Vanderbilt University"
              inputClassName="w-full rounded-xl border border-[#bfdbfe] px-4 py-3 focus:outline-none focus:border-[#1d4ed8]"
              testId="dream-school-input"
            />
          </div>
          <div className="mb-4">
            <ResidencyToggle
              value={residency}
              onChange={setResidency}
              testId="dream-residency"
            />
          </div>
          <label className="block text-xs font-medium text-slate-500 mb-1.5">Why do you think it's out of reach? (optional)</label>
          <textarea value={why} onChange={(e) => setWhy(e.target.value)} rows={2} data-testid="dream-why-input"
            placeholder="e.g. The sticker price is $90,000 and we make $85k — feels impossible"
            className="w-full rounded-xl border border-[#bfdbfe] px-4 py-3 focus:outline-none focus:border-[#1d4ed8] mb-4"/>
          {error && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4" data-testid="dream-error">{error}</div>}
          <button type="submit" disabled={busy || !school || !residency} data-testid="dream-run-btn"
            className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af] text-white text-sm font-medium px-6 py-3 disabled:opacity-60">
            {busy ? "Running rescue plan…" : "Show me how I actually get there →"}
          </button>
        </form>

        {busy && !plan && (
          <Spinner variant="block" label="Reasoning over admissions, aid, and your specific gap." testId="dream-spinner" />
        )}

        {plan && <Plan plan={plan} />}
      </div>
    </AppShell>
  );
}

function Plan({ plan }) {
  const v = VERDICT[plan.verdict] || VERDICT.realistic;
  return (
    <div className="space-y-6" data-testid="dream-plan">
      <div className="aw-card bg-white border border-[#bfdbfe] rounded-2xl p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${v.cls}`} data-testid="dream-verdict">{v.label}</span>
        </div>
        <h2 className="font-heading italic text-3xl text-[#0f172a] mb-5">{plan.headline}</h2>
        <div className="mb-6">
          <StickerVsNet sticker={plan.sticker_price_usd} net={plan.estimated_net_price_usd} testId="dream-realcost" />
        </div>
        <p className="text-[#0f172a] leading-relaxed whitespace-pre-line">{plan.story}</p>
      </div>

      {Array.isArray(plan.steps) && plan.steps.length > 0 && (
        <div className="aw-card bg-white border border-[#bfdbfe] rounded-2xl p-6 sm:p-8">
          <h3 className="font-heading italic text-2xl text-[#0f172a] mb-4">Your steps</h3>
          <ol className="space-y-4">
            {plan.steps.map((s, i) => (
              <li key={i} className="flex items-start gap-4" data-testid={`dream-step-${i}`}>
                <div className="rounded-full bg-[#eff6ff] text-[#1d4ed8] w-8 h-8 flex items-center justify-center text-sm font-medium shrink-0">{i + 1}</div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#0f172a]">{s.step}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{s.why}</div>
                </div>
                {s.savings_usd > 0 && (
                  <div className="text-sm font-mono text-emerald-700 shrink-0">${s.savings_usd.toLocaleString()}</div>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {Array.isArray(plan.scholarships) && plan.scholarships.length > 0 && (
        <div className="aw-card bg-white border border-[#bfdbfe] rounded-2xl p-6 sm:p-8" data-testid="dream-scholarships">
          <h3 className="font-heading italic text-2xl text-[#0f172a] mb-4">Scholarships specific to {plan.school}</h3>
          <ul className="space-y-3">
            {plan.scholarships.map((s, i) => (
              <li key={i} className="flex items-baseline justify-between gap-4 border-b border-[#e5e7eb] pb-3 last:border-0">
                <div>
                  <div className="text-sm font-medium text-[#0f172a]">{s.name}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{s.eligibility}</div>
                  {s.url && <div className="text-[10px] text-slate-400 mt-0.5">{s.url}</div>}
                </div>
                <div className="text-sm font-mono text-emerald-700 shrink-0">${(s.amount_usd || 0).toLocaleString()}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="aw-card bg-white border border-[#bfdbfe] rounded-2xl p-6">
          <div className="text-xs uppercase tracking-widest text-[#1d4ed8] mb-2">Appeal potential</div>
          <div className="text-base font-medium text-[#0f172a]" data-testid="dream-appeal">{APPEAL[plan.appeal_potential] || plan.appeal_potential}</div>
          {plan.appeal_note && <p className="text-sm text-slate-600 mt-2">{plan.appeal_note}</p>}
        </div>
        <div className="aw-card bg-white border border-[#bfdbfe] rounded-2xl p-6">
          <div className="text-xs uppercase tracking-widest text-[#1d4ed8] mb-2">If direct admit doesn't pan out</div>
          <p className="text-sm text-slate-600 leading-relaxed" data-testid="dream-transfer">{plan.transfer_path}</p>
        </div>
      </div>
    </div>
  );
}
